import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Printer, 
  Package, 
  Search, 
  Globe, 
  Palette, 
  ShoppingBag,
  Check,
  Truck,
  BarChart,
  Boxes,
  Tag,
  Headphones,
  RefreshCw,
  Users,
  Layers
} from 'lucide-react';

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle anchor navigation
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          const yOffset = -100; // Account for fixed header
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'print-on-demand',
      title: 'Print on Demand',
      icon: Printer,
      color: 'bg-green-500',
      tagline: 'Custom Printing Without Inventory Risk',
      description: 'Our Print on Demand service allows you to sell custom-designed products without holding inventory. We handle printing, quality control, and shipping while you focus on growing your brand.',
      features: [
        'No minimum order quantities',
        'Wide range of printable products',
        'High-quality printing technology',
        'Custom packaging options',
        'Fast turnaround times',
        'Automated order processing'
      ],
      benefits: [
        { icon: Boxes, text: 'Zero inventory costs' },
        { icon: RefreshCw, text: 'Easy product testing' },
        { icon: BarChart, text: 'Scalable operations' }
      ],
      image: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'dropshipping',
      title: 'Dropshipping Fulfillment',
      icon: Package,
      color: 'bg-blue-500',
      tagline: 'Seamless Order Fulfillment for Your Store',
      description: 'Our dropshipping fulfillment service handles the entire order process from warehouse to customer doorstep. We pick, pack, and ship your orders with care and precision.',
      features: [
        'Same-day order processing',
        'Real-time inventory sync',
        'Multiple shipping options',
        'Branded packing slips',
        'Return management',
        'Order tracking integration'
      ],
      benefits: [
        { icon: Truck, text: 'Fast shipping worldwide' },
        { icon: BarChart, text: 'Inventory management' },
        { icon: Headphones, text: '24/7 support' }
      ],
      image: 'https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'product-sourcing',
      title: 'Product Sourcing',
      icon: Search,
      color: 'bg-purple-500',
      tagline: 'Find Quality Products at Competitive Prices',
      description: 'Our product sourcing team helps you find reliable suppliers and quality products at the best prices. We handle negotiations, quality inspections, and import logistics.',
      features: [
        'Vetted supplier network',
        'Quality inspection services',
        'Competitive price negotiations',
        'Sample procurement',
        'Import/export assistance',
        'Product customization options'
      ],
      benefits: [
        { icon: Tag, text: 'Better margins' },
        { icon: Users, text: 'Trusted suppliers' },
        { icon: Layers, text: 'Product variety' }
      ],
      image: 'https://images.pexels.com/photos/5632398/pexels-photo-5632398.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'global-warehousing',
      title: 'Global Warehousing',
      icon: Globe,
      color: 'bg-orange-500',
      tagline: 'Strategic Storage for Faster Delivery',
      description: 'Store your inventory in our strategically located warehouses worldwide. Reduce shipping times and costs while improving customer satisfaction with local fulfillment.',
      features: [
        'Multiple warehouse locations',
        'Climate-controlled storage',
        'Real-time inventory tracking',
        'Automated reorder alerts',
        'Kitting and bundling services',
        'Cross-docking capabilities'
      ],
      benefits: [
        { icon: Globe, text: 'Global reach' },
        { icon: Truck, text: 'Reduced shipping times' },
        { icon: BarChart, text: 'Lower costs' }
      ],
      image: 'https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'custom-branding',
      title: 'Custom Branding',
      icon: Palette,
      color: 'bg-pink-500',
      tagline: 'Create a Memorable Unboxing Experience',
      description: 'Elevate your brand with our custom branding solutions. From custom packaging to branded inserts, we help you create a memorable customer experience.',
      features: [
        'Custom packaging design',
        'Branded shipping materials',
        'Thank you cards & inserts',
        'Custom labels and stickers',
        'Gift wrapping options',
        'Sustainable packaging choices'
      ],
      benefits: [
        { icon: Users, text: 'Brand recognition' },
        { icon: RefreshCw, text: 'Repeat customers' },
        { icon: Tag, text: 'Premium positioning' }
      ],
      image: 'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 'tiktok-shop',
      title: 'TikTok Shop Solutions',
      icon: ShoppingBag,
      color: 'bg-red-500',
      tagline: 'Fulfillment Optimized for Social Commerce',
      description: 'Specialized fulfillment services for TikTok Shop sellers. We understand the unique demands of social commerce and provide fast, reliable fulfillment to keep up with viral trends.',
      features: [
        'Rapid order processing',
        'TikTok Shop integration',
        'Trending product fulfillment',
        'Live stream order handling',
        'Influencer collaboration support',
        'Flash sale capabilities'
      ],
      benefits: [
        { icon: Truck, text: 'Fast turnaround' },
        { icon: BarChart, text: 'Scale with trends' },
        { icon: Headphones, text: 'Dedicated support' }
      ],
      image: 'https://images.pexels.com/photos/5632388/pexels-photo-5632388.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Chivo'] font-bold text-gray-900 mb-6" data-testid="services-hero-title">
              Comprehensive{' '}
              <span className="text-green-600">Fulfillment Solutions</span>
            </h1>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              From Print on Demand to Global Warehousing, we provide everything you need 
              to scale your ecommerce business efficiently and effectively.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Navigation */}
      <section className="sticky top-20 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 py-4 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-center gap-2 flex-wrap">
            {services.map((service) => (
              <a
                key={service.id}
                href={`#${service.id}`}
                data-testid={`nav-${service.id}`}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors"
              >
                {service.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          data-testid={`service-section-${service.id}`}
          className={`py-20 md:py-28 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:col-start-2' : ''}
              >
                <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-6`}>
                  <service.icon size={28} className="text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-['Chivo'] font-bold text-gray-900 mb-3">
                  {service.title}
                </h2>
                <p className="text-green-600 font-medium mb-4">{service.tagline}</p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check size={18} className="text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div className="flex flex-wrap gap-4 mb-8">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                      <benefit.icon size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-gray-700">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  data-testid={`cta-${service.id}`}
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                >
                  Get Started
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`relative ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className={`absolute -z-10 w-full h-full ${service.color} opacity-10 rounded-2xl ${index % 2 === 0 ? 'top-4 left-4' : 'top-4 right-4'}`} />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Chivo'] font-bold text-white mb-6">
              Need a Custom{' '}
              <span className="text-green-500">Solution?</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-10 leading-relaxed">
              Don't see exactly what you need? Our team can create a customized fulfillment 
              solution tailored to your specific business requirements.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
            >
              Contact Our Team
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
